import classes from "./Backup.module.css"
import { faClipboard, faUpload, faDownload, faTrash, faFileImport, faFileCode } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useLocalStorage } from "../../../Utils"
import React, { useState, useCallback, useMemo } from "react"
import Modal from "../../../components/UI/Modal/Modal"
import headData from "../../../assets/json/parameters/GearInfoHead.json"
import clothesData from "../../../assets/json/parameters/GearInfoClothes.json"
import shoesData from "../../../assets/json/parameters/GearInfoShoes.json"
import { SkillIDsRev } from "../../../Utils"
import { get_full_data_struct, useBoolState, dict_length } from "../../../Utils"
import { useTranslation } from "react-i18next"
import { defaultGearList, defaultImportData, defaultProfile } from "../../../DefaultEntries"

const Backup = props => {
    const { t } = useTranslation()

    const [gearList, setGearList] = useLocalStorage("gearList", defaultGearList)
    const [userData, setUserData] = useLocalStorage("importData", defaultImportData)
    const [profile, setProfile] = useLocalStorage("profile", defaultProfile)
    const [uploadOpen, onOpen, onClose] = useBoolState()
    const copyToClipboard = useCallback(
        () =>
            navigator.clipboard
                .writeText(JSON.stringify(get_full_data_struct()))
                .then(() => alert(t("misc:Common.CopyToClipboard")))
                .catch(console.error),
        // eslint-disable-next-line
        [t, gearList, userData, profile]
    )

    const deleteData = useCallback(() => {
        if (!window.confirm(t("misc:Common.DeleteConfirmation"))) return

        setGearList(defaultGearList)
        setUserData(defaultImportData)
        setProfile(defaultProfile)
    }, [setGearList, t, setUserData, setProfile])

    const downloadData = useCallback(() => {
        const date = new Date()
        const dateStr = date.toISOString().split(".")[0].replace("T", "_").replaceAll(":", "-")
        const JSONStr = JSON.stringify(get_full_data_struct())
        const filename = `gear_seed_database_${dateStr}.json`
        const contentType = "application/json;charset=utf-8"
        const a = document.createElement("a")
        a.download = filename
        a.href = `data:${contentType},${encodeURIComponent(JSONStr)}`
        a.target = "_blank"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        // eslint-disable-next-line
    }, [gearList, userData])

    const onUpload = useCallback(
        newDB => {
            setGearList(newDB.GearDB)
            setUserData(newDB.UserData)
            setProfile(newDB.Profile)
            onClose()
        },
        [onClose, setGearList, setUserData, setProfile]
    )

    const hasData =
        dict_length(gearList.HaveGearHeadMap) > 0 ||
        dict_length(gearList.HaveGearClothesMap) > 0 ||
        dict_length(gearList.HaveGearShoesMap) > 0 > 0

    const headSeeds = Object.assign(
        {},
        ...Object.entries(gearList.HaveGearHeadMap)
            .filter(([k, v]) => v.RandomContext > 0)
            .map(([k, v]) => ({ [k]: v }))
    )
    const clothesSeeds = Object.assign(
        {},
        ...Object.entries(gearList.HaveGearClothesMap)
            .filter(([k, v]) => v.RandomContext > 0)
            .map(([k, v]) => ({ [k]: v }))
    )
    const shoesSeeds = Object.assign(
        {},
        ...Object.entries(gearList.HaveGearShoesMap)
            .filter(([k, v]) => v.RandomContext > 0)
            .map(([k, v]) => ({ [k]: v }))
    )

    const foundSeeds = dict_length(headSeeds) + dict_length(clothesSeeds) + dict_length(shoesSeeds)

    return (
        <>
            <div className={classes.cardWrapperWrapper}>
                <div className={classes.cardWrapper}>
                    <div className={classes.Subtitle}>
                        <h6 className={classes.Subtitleh6}>{t("misc:Common.Database")}</h6>
                    </div>
                    <hr className={classes.Divider} />
                    <div className={classes.CardContent}>
                        <div className={classes.CardDark}>
                            <div>
                                <div>
                                    <p className={classes.TextDescription}>
                                        {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_01-T_Category_00")}:{" "}
                                        <strong>{dict_length(gearList.HaveGearHeadMap)}</strong>
                                    </p>
                                    <p className={classes.TextDescription}>
                                        {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_02-T_Category_00")}:{" "}
                                        <strong>{dict_length(gearList.HaveGearClothesMap)}</strong>
                                    </p>
                                    <p className={classes.TextDescription}>
                                        {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_03-T_Category_00")}:{" "}
                                        <strong>{dict_length(gearList.HaveGearShoesMap)}</strong>
                                    </p>
                                    <p className={classes.TextDescription}>
                                        {t("misc:Common.SeedsFound")}: <strong>{foundSeeds}</strong>
                                    </p>
                                </div>
                                <div style={{ marginTop: 16 }}>
                                    <div className={classes.GridStyle}>
                                        <div className={classes.GridElem}>
                                            <button
                                                disabled={!hasData}
                                                onClick={copyToClipboard}
                                                className={hasData ? classes.ButtonInfo : classes.ButtonDisabled}
                                            >
                                                <FontAwesomeIcon icon={faClipboard} />{" "}
                                                <span style={{ padding: 6 }}>{t("misc:Common.Clipboard")}</span>
                                            </button>
                                        </div>
                                        <div className={classes.GridElem}>
                                            <Modal show={uploadOpen} modalClosed={onClose} wide={true}>
                                                <UploadCard onReplace={onUpload} />
                                            </Modal>
                                            <button onClick={onOpen} className={classes.ButtonInfo}>
                                                <FontAwesomeIcon icon={faUpload} />{" "}
                                                <span style={{ padding: 6 }}>{t("misc:Common.Upload")}</span>
                                            </button>
                                        </div>
                                        <div className={classes.GridElem}>
                                            <button
                                                disabled={!hasData}
                                                onClick={downloadData}
                                                className={hasData ? classes.ButtonDefault : classes.ButtonDisabled}
                                            >
                                                <FontAwesomeIcon icon={faDownload} />{" "}
                                                <span style={{ padding: 6 }}>{t("misc:Common.Download")}</span>
                                            </button>
                                        </div>
                                        <div className={classes.GridElem}>
                                            <button
                                                disabled={!hasData}
                                                onClick={deleteData}
                                                className={hasData ? classes.ButtonError : classes.ButtonDisabled}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />{" "}
                                                <span style={{ padding: 6 }}>{t("misc:Common.Delete")}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

const UploadCard = props => {
    const { t } = useTranslation()

    const [data, setdata] = useState("")
    const [filename, setfilename] = useState("")
    const [errorMsg, setErrorMsg] = useState("")
    const [userData] = useLocalStorage("importData", defaultImportData)

    const onUpload = async e => {
        const file = e.target.files[0]
        e.target.value = null // reset the value so the same file can be uploaded again...
        if (file) setfilename(file.name)
        const reader = new FileReader()
        reader.onload = () => setdata(reader.result)
        reader.readAsText(file)
    }

    const importedDatabase = useMemo(() => {
        if (!data) return
        let parsed
        try {
            parsed = JSON.parse(data)
            if (typeof parsed !== "object") {
                setErrorMsg(t("misc:Common.ErrorJSONParse"))
                return
            }
        } catch (e) {
            setErrorMsg(t("misc:Common.ErrorJSONParse"))
            return
        }
        let tmp_data = parsed
        if ("gear" in tmp_data) {
            setErrorMsg(t("misc:Database.UseSplatnetUpload"))
            return
        }
        if (Array.isArray(parsed)) {
            if (parsed.length === 2) {
                tmp_data = parsed[1]
            } else {
                setErrorMsg(t("misc:Database.ErrorInvalidFormat"))
                return
            }
        }
        // parse geardata
        let head, clothes, shoes
        if ("HaveGearHeadMap" in tmp_data && "HaveGearClothesMap" in tmp_data && "HaveGearShoesMap" in tmp_data) {
            head = tmp_data.HaveGearHeadMap
            clothes = tmp_data.HaveGearClothesMap
            shoes = tmp_data.HaveGearShoesMap
        } else if ("GearDB" in tmp_data) {
            head = tmp_data.GearDB.HaveGearHeadMap ?? {}
            clothes = tmp_data.GearDB.HaveGearClothesMap ?? {}
            shoes = tmp_data.GearDB.HaveGearShoesMap ?? {}
        } else {
            setErrorMsg(t("misc:Database.ErrorInvalidFormat"))
            return
        }
        const use_head = {}
        const use_clothes = {}
        const use_shoes = {}
        for (const [key, val] of Object.entries(head)) {
            const h = headData.filter(e => e.Id === parseInt(key))
            if (h.length > 0) {
                use_head[key] = {
                    ExSkillArray: val.ExSkillArray ?? [],
                    ExDrinksArray: val.ExDrinksArray ?? [],
                    MainSkill: val.MainSkill ?? SkillIDsRev[h.Skill],
                    RandomContext: val.RandomContext ?? 0,
                }
            }
        }
        for (const [key, val] of Object.entries(clothes)) {
            const h = clothesData.filter(e => e.Id === parseInt(key))
            if (h.length > 0) {
                use_clothes[key] = {
                    ExSkillArray: val.ExSkillArray ?? [],
                    ExDrinksArray: val.ExDrinksArray ?? [],
                    MainSkill: val.MainSkill ?? SkillIDsRev[h.Skill],
                    RandomContext: val.RandomContext ?? 0,
                }
            }
        }
        for (const [key, val] of Object.entries(shoes)) {
            const h = shoesData.filter(e => e.Id === parseInt(key))
            if (h.length > 0) {
                use_shoes[key] = {
                    ExSkillArray: val.ExSkillArray ?? [],
                    ExDrinksArray: val.ExDrinksArray ?? [],
                    MainSkill: val.MainSkill ?? SkillIDsRev[h.Skill],
                    RandomContext: val.RandomContext ?? 0,
                }
            }
        }
        setErrorMsg("")

        // parse Seeds
        let user_res = { ...userData }

        if ("UserKey" in tmp_data && "H" in tmp_data) {
            user_res["UserKey"] = tmp_data.UserKey
            user_res["H"] = tmp_data.H
        }

        if ("CloudRandomSet" in tmp_data) {
            user_res["CloudRandomSet"] = tmp_data.CloudRandomSet
        }

        if ("UserData" in tmp_data) {
            user_res["CloudRandomSet"] = tmp_data.UserData.CloudRandomSet ?? {}
            user_res["UserKey"] = tmp_data.UserData.UserKey ?? ""
            user_res["H"] = tmp_data.UserData.H ?? 0
        }

        // parse badges
        let profile = defaultProfile

        if ("LastPlayHaveBadgeMap" in tmp_data) {
            profile.BadgeList = Object.keys(tmp_data.LastPlayHaveBadgeMap)
        }
        if ("Profile" in tmp_data && "BadgeList" in tmp_data.Profile) {
            profile.BadgeList = tmp_data.Profile.BadgeList
        }

        if ("HaveGoodsMap" in tmp_data) {
            profile.GoodsMap = tmp_data.HaveGoodsMap
        }
        if ("Profile" in tmp_data && "GoodsMap" in tmp_data.Profile) {
            profile.GoodsMap = tmp_data.Profile.GoodsMap
        }

        if ("HaveBynameAdjectiveMap" in tmp_data) {
            profile.BynameAdjectiveMap = tmp_data.HaveBynameAdjectiveMap
        }
        if ("Profile" in tmp_data && "BynameAdjectiveMap" in tmp_data.Profile) {
            profile.BynameAdjectiveMap = tmp_data.Profile.BynameAdjectiveMap
        }

        if ("HaveBynameAdjectiveMap" in tmp_data) {
            profile.EmoteMap = tmp_data.HaveEmoteMap
        }
        if ("Profile" in tmp_data && "EmoteMap" in tmp_data.Profile) {
            profile.EmoteMap = tmp_data.Profile.EmoteMap
        }

        if ("HaveBynameSubjectMap" in tmp_data) {
            profile.BynameSubjectMap = tmp_data.HaveBynameSubjectMap
        }
        if ("Profile" in tmp_data && "BynameSubjectMap" in tmp_data.Profile) {
            profile.BynameSubjectMap = tmp_data.Profile.BynameSubjectMap
        }

        if ("HaveNamePlateBgMap" in tmp_data) {
            profile.NamePlateBgMap = {}
            for (var key of Object.keys(tmp_data.HaveNamePlateBgMap)) {
                profile.NamePlateBgMap[key] = true
            }
        }
        if ("Profile" in tmp_data && "NamePlateBgMap" in tmp_data.Profile) {
            profile.NamePlateBgMap = tmp_data.Profile.NamePlateBgMap
        }

        if ("HaveSkillChipMap" in tmp_data) {
            profile.SkillChipMap = Object.fromEntries(
                Object.entries(tmp_data.HaveSkillChipMap).filter(([k, v]) => k < 200)
            )
        }
        if ("Profile" in tmp_data && "SkillChipMap" in tmp_data.Profile) {
            profile.SkillChipMap = tmp_data.Profile.SkillChipMap
        }

        if ("Versus" in tmp_data) {
            profile.Versus = {
                WinCountByRule: tmp_data.Versus.WinCountByRule,
                WinCountBySpecial: tmp_data.Versus.WinCountBySpecial,
                WinCountByTclAtk: tmp_data.Versus.WinCountByTclAtk,
                WinCountByTclDef: tmp_data.Versus.WinCountByTclDef,
            }
        }
        if ("Profile" in tmp_data && "Versus" in tmp_data.Profile) {
            profile.Versus = tmp_data.Profile.Versus
        }

        if ("Coop" in tmp_data) {
            profile.Coop = {
                BossKillNumMap: tmp_data.Coop.BossKillNumMap,
                RareEnemyKillNumMap: tmp_data.Coop.RareEnemyKillNumMap,
            }
        }
        if ("Profile" in tmp_data && "Coop" in tmp_data.Profile) {
            profile.Coop = tmp_data.Profile.Coop
        }

        if ("PlayerRank" in tmp_data) {
            profile.Level = tmp_data.PlayerRank
        }

        if ("Profile" in tmp_data && "Level" in tmp_data.Profile) {
            profile.Level = tmp_data.Profile.Level
        }

        const res = {
            GearDB: {
                HaveGearHeadMap: use_head,
                HaveGearClothesMap: use_clothes,
                HaveGearShoesMap: use_shoes,
            },
            UserData: user_res,
            Profile: profile,
        }
        return res
    }, [data, t, userData])

    const replaceDB = useCallback(() => {
        if (!importedDatabase) return
        setdata("")
        setfilename("")
        props.onReplace(importedDatabase)
    }, [importedDatabase, props])

    return (
        <>
            <div className={classes.CardContent}>{t("misc:Common.DatabaseUpload")}</div>
            <div className={classes.CardContent}>
                <div className={classes.GridStyle}>
                    <div style={{ paddingLeft: 16, paddingTop: 16 }}>
                        <label>
                            <input
                                id="icon-button-file"
                                accept=".json"
                                type="file"
                                style={{ display: "none" }}
                                onChange={onUpload}
                            />

                            <span className={classes.ButtonDefault}>
                                <FontAwesomeIcon icon={faFileImport} style={{ marginRight: 8 }} />
                                {t("misc:Database.ButtonOpen")}
                            </span>
                        </label>
                    </div>
                    <div style={{ paddingLeft: 16, paddingTop: 16 }}>
                        <div style={{ paddingLeft: 16, paddingTop: 6 }}>
                            {filename ? (
                                <span>
                                    <FontAwesomeIcon icon={faFileCode} style={{ marginRight: 8 }} /> {filename}
                                </span>
                            ) : (
                                <span>{t("misc:Database.UploadJson")}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div></div>
                <span className={classes.TextTiny}>{t("misc:Database.UploadJsonPath")}</span>
                <textarea value={data} onChange={e => setdata(e.target.value)}></textarea>
                {!importedDatabase ? (
                    errorMsg
                ) : (
                    <div style={{ marginTop: 16 }}>
                        <div>
                            <div>
                                {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_01-T_Category_00")}:{" "}
                                <strong>{dict_length(importedDatabase.GearDB.HaveGearHeadMap)}</strong>
                            </div>
                            <div>
                                {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_02-T_Category_00")}:{" "}
                                <strong>{dict_length(importedDatabase.GearDB.HaveGearClothesMap)}</strong>
                            </div>
                            <div>
                                {t("translation:LayoutMsg.Cmn_CstBase_00.L_BtnCategory_03-T_Category_00")}:{" "}
                                <strong>{dict_length(importedDatabase.GearDB.HaveGearShoesMap)}</strong>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <hr className={classes.Divider} />
            <div className={classes.CardContent}>
                <button
                    className={importedDatabase ? classes.ButtonInfo : classes.ButtonDisabled}
                    disabled={!importedDatabase}
                    onClick={replaceDB}
                >
                    <FontAwesomeIcon icon={faUpload} />{" "}
                    <span style={{ padding: 6 }}>{t("misc:Common.UpdateDatabase")}</span>
                </button>
            </div>
        </>
    )
}

export default Backup
